import { getToken, onMessage } from "firebase/messaging";
import { useEffect } from "react";
import { toast } from "sonner";

import {
  getFirebaseMessaging,
  isFirebaseConfigured,
} from "@/core/notifications/firebase";
import {
  registerNotificationDevice,
  unregisterNotificationDeviceBestEffort,
} from "@/core/notifications/notification-devices";
import { useNotificationStore } from "@/core/stores/notification.store";
import { useSessionStore } from "@/core/stores/session.store";
import { logger } from "@/core/utils/func/logger";

/** Survit au double-mount StrictMode : le setup ne s'exécute qu'une fois. */
let started = false;

/** Même mécanisme audio que `notifClarSound` (`core/utils/func/notifApp.tsx`). */
function playNotificationSound() {
  const audio = new Audio("/audio/notification-sound.wav");
  audio.volume = 1;
  audio.play().catch((err) => {
    console.warn("Le son n'a pas pu être joué :", err);
  });
}

/**
 * Branche Firebase Cloud Messaging pour l'utilisateur connecté :
 * enregistre le service worker, demande la permission, récupère le token
 * FCM, l'envoie au notification-service (`NotificationDevices/add`) et
 * alimente le store local (+ toast sonner + son) à chaque message reçu
 * au premier plan. Monté une fois dans `App.tsx`, auto-gâté par
 * l'authentification.
 */
export function useFcmNotifications() {
  const isAuthenticated = useSessionStore((s) => Boolean(s.accessToken));

  useEffect(() => {
    if (!isAuthenticated) {
      started = false;
      return;
    }
    if (started) return;

    if (!isFirebaseConfigured()) {
      logger.warn(
        "Configuration Firebase incomplète (VITE_FIREBASE_*) — notifications push désactivées.",
        { prefix: "FCM" }
      );
      return;
    }

    started = true;

    const setup = async () => {
      const { addNotification, setFcmToken, setPermission, fcmToken } =
        useNotificationStore.getState();

      const messaging = await getFirebaseMessaging();
      if (!messaging) {
        setPermission("unsupported");
        logger.warn(
          "Firebase Messaging non supporté par ce navigateur.",
          { prefix: "FCM" }
        );
        return;
      }

      const serviceWorkerRegistration =
        await navigator.serviceWorker.register("/firebase-messaging-sw.js");

      if (Notification.permission === "default") {
        await Notification.requestPermission();
      }
      setPermission(Notification.permission);

      if (Notification.permission === "granted") {
        const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
        if (!vapidKey) {
          logger.warn(
            "VITE_FIREBASE_VAPID_KEY manquant — récupération du token FCM impossible.",
            { prefix: "FCM" }
          );
        } else {
          try {
            const token = await getToken(messaging, {
              vapidKey,
              serviceWorkerRegistration,
            });
            if (token) {
              setFcmToken(token);
              try {
                await registerNotificationDevice(token);
                logger.info(
                  "Appareil FCM enregistré auprès du notification-service.",
                  { prefix: "FCM" }
                );
              } catch (error) {
                logger.warn("Échec d'enregistrement du token FCM.", {
                  prefix: "FCM",
                  data: error,
                });
              }
            }
          } catch (error) {
            logger.warn("Échec de récupération du token FCM.", {
              prefix: "FCM",
              data: error,
            });
          }
        }
      } else if (Notification.permission === "denied" && fcmToken) {
        void unregisterNotificationDeviceBestEffort();
        setFcmToken(null);
      }

      onMessage(messaging, (payload) => {
        const title = payload.notification?.title ?? "";
        const body = payload.notification?.body ?? "";
        addNotification({ title, body, data: payload.data });
        playNotificationSound();
        toast.info(title || body, {
          description: title ? body : undefined,
        });
      });
    };

    void setup();
  }, [isAuthenticated]);
}
